import React, { useEffect, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { ICON_SIZE } from 'components/common/constants';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CalendarMaskedInputProps {
    id: string;
    /** What the user has entered so far — drawn as normal input text. */
    typed: string;
    /** Untouched tail of the mask — drawn as grey italic ghost text. */
    ghost: string;
    /** Native placeholder. Only used when a consumer overrides the mask ghost. */
    placeholder?: string;
    disabled?: boolean;
    isOpen: boolean;
    ariaLabel: string;
    toggleLabel: string;
    className?: string;
    /** Explicit tab position, for consumers that order a whole form by hand. */
    tabIndex?: number;
    inputRef: React.RefObject<HTMLInputElement>;
    onValueChange: (value: string) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    /** Opening the picker: focusing or clicking the field never closes it. */
    onOpen: () => void;
    /** The calendar button toggles, so it can also close the picker. */
    onToggle: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * The calendar's editable field: a plain text input holding the digits typed so
 * far, with the rest of the dd-mm-yyyy mask suggested behind it as ghost text.
 *
 * The ghost layer sits underneath the (transparent-background) input and starts
 * with an invisible copy of the typed text. That copy is rendered with the very
 * same font metrics as the input, so the suggestion always begins exactly where
 * the caret is and the two layers read as a single value.
 */
export const CalendarMaskedInput: React.FC<CalendarMaskedInputProps> = ({
    id,
    typed,
    ghost,
    placeholder,
    disabled = false,
    isOpen,
    ariaLabel,
    toggleLabel,
    className,
    tabIndex,
    inputRef,
    onValueChange,
    onKeyDown,
    onOpen,
    onToggle,
}) => {
    // A field too narrow for the whole value scrolls its text; the ghost layer has
    // to follow, or the suggestion drifts away from the caret.
    const [scrollLeft, setScrollLeft] = useState(0);
    useEffect(() => setScrollLeft(inputRef.current?.scrollLeft ?? 0), [typed, inputRef]);

    return (
    <div className={`ga-input new-calendar-input${className ? ` ${className}` : ''}`}>
        <span className="new-calendar-input__box">
            <input
                className="ga-input__field new-calendar-input__field"
                id={id}
                data-testid={id}
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={typed}
                placeholder={placeholder}
                disabled={disabled}
                {...(tabIndex !== undefined && {tabIndex})}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                onChange={event => onValueChange(event.target.value)}
                onKeyDown={onKeyDown}
                onFocus={onOpen}
                onClick={onOpen}
                onScroll={event => setScrollLeft(event.currentTarget.scrollLeft)}
            />
            {!!ghost && (
                <span
                    className="new-calendar-input__ghost"
                    aria-hidden="true"
                    style={{transform: `translateX(${-scrollLeft}px)`}}
                >
                    <span className="new-calendar-input__ghost-typed">{typed}</span>
                    <span className="new-calendar-input__ghost-text">{ghost}</span>
                </span>
            )}
        </span>
        <button
            type="button"
            className="new-calendar-input__toggle"
            aria-label={toggleLabel}
            aria-expanded={isOpen}
            disabled={disabled}
            tabIndex={-1}
            // Keep the caret in the field when the button is used to open the picker.
            onMouseDown={event => event.preventDefault()}
            onClick={onToggle}
        >
            <CalendarIcon size={ICON_SIZE.SMALL} />
        </button>
    </div>
    );
};

export default CalendarMaskedInput;
